import { Box } from '@mui/material';
import { isAfter } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { TrlFormSectionSeparator, TrlPill } from '»components/atoms';
import {
  TrlDatePicker,
  TrlFormDropdown,
  TrlTextField,
} from '»components/atoms/Inputs';
import {
  onboardingMainFormFieldsSelector,
  onboardingSlice,
} from '»components/Auth/Onboarding/redux';
import { onboardingOpeningRequestIdSelector } from '»components/Auth/Onboarding/redux/onboarding.selectors';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import { TrlFormRadioGroup } from '»components/molecules/TrlFormRadioGroup';
import { isAureaActiveClient } from '»utils/clientsUtils';
import { useFieldPropertyVerification } from '»utils/customHooks';
import { isSubmitFormButtonDisabled } from '»utils/form';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { FormStyled } from '../../../../Onboarding.styles';
import { FormWrapper } from '../../../FormWrapper';
import {
  ElementContainer,
  ElementHeader,
  ElementLabel,
} from '../LegalPersonForms.styles';
import { repreFormValidation } from './RepreForm.yup';

export const RepreForm = () => {
  const dispatch = useDispatch();

  const mainFormFields = useSelector(onboardingMainFormFieldsSelector);
  const openingRequestId = useSelector(onboardingOpeningRequestIdSelector);

  const firstRepresentative = mainFormFields.find(
    (field) => field.name === 'representatives',
  ).data[0];

  const { verifyFieldProperty } =
    useFieldPropertyVerification(firstRepresentative);

  const yesLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-YES',
  });

  const noLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-NO',
  });

  const validityExpiredLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-VALIDITY_EXPIRED',
  });

  const todaysDate = new Date();
  todaysDate.setHours(0, 0, 0, 0);

  const isFieldHidden = {
    repOtherIdType: verifyFieldProperty({
      fieldName: 'repOtherIdType',
      fieldProperty: 'isHidden',
    }),
    repIdNum: verifyFieldProperty({
      fieldName: 'repIdNum',
      fieldProperty: 'isHidden',
    }),
    repIdLocal: verifyFieldProperty({
      fieldName: 'repIdLocal',
      fieldProperty: 'isHidden',
    }),
    repIdDate: verifyFieldProperty({
      fieldName: 'repIdDate',
      fieldProperty: 'isHidden',
    }),
    repIdExpDate: verifyFieldProperty({
      fieldName: 'repIdExpDate',
      fieldProperty: 'isHidden',
    }),
    repUsTaxId: verifyFieldProperty({
      fieldName: 'repUsTaxId',
      fieldProperty: 'isHidden',
    }),
    repNoApplOpt: verifyFieldProperty({
      fieldName: 'repNoApplOpt',
      fieldProperty: 'isHidden',
    }),
    repPpeKinshipDeg: verifyFieldProperty({
      fieldName: 'repPpeKinshipDeg',
      fieldProperty: 'isHidden',
    }),
  };

  const hasInitialValue = (fieldName, repreIndex) => {
    const fieldsApiInitialValue = mainFormFields
      .find((field) => field.name === 'representatives')
      .data[repreIndex]?.reduce((acc, field) => {
        acc[field.name] = field.apiInitialValue;
        return acc;
      }, {});

    if (!fieldsApiInitialValue) return false;

    return (
      fieldsApiInitialValue[fieldName] !== '' ||
      fieldsApiInitialValue[fieldName].length !== 0
    );
  };

  const getFieldOptions = (currentFieldName) => {
    const representatives = mainFormFields.find(
      (field) => field.name === 'representatives',
    );

    const currentField = representatives.data[0].find(
      (field) => field.name === currentFieldName,
    );

    return currentField?.options ?? [];
  };

  const handleSubmit = (formValues, { setSubmitting }) => {
    const disableFormSubmittingState = () => {
      setSubmitting(false);
    };

    dispatch(
      onboardingSlice.actions.submitFinancialContractRequest({
        openingRequestId,
        formValues,
        disableFormSubmittingState,
      }),
    );
  };

  const setRepNoApplOptValue = ({
    event,
    fieldsNamePrefix,
    currentFieldNameSufix,
    values,
    setFieldValue,
  }) => {
    const repNoApplOptFieldName = `${fieldsNamePrefix}.repNoApplOpt`;

    if (event.target.value === 'Y') {
      setFieldValue(repNoApplOptFieldName, 'N', false);
    } else {
      const repNoApplOpt = Object.keys(values).find(
        (field) =>
          field !== currentFieldNameSufix &&
          (field === 'repPpe' ||
            field === 'repPpeFamily' ||
            field === 'repPpeRelationship') &&
          (values[field] === '' || values[field] === 'Y'),
      )
        ? 'N'
        : 'Y';

      setFieldValue(repNoApplOptFieldName, repNoApplOpt, false);
    }
  };

  return (
    <FormWrapper
      handleSubmitForm={handleSubmit}
      formValidation={repreFormValidation}
    >
      {({ isSubmitting, dirty, errors, values, setFieldValue }) => (
        <FormStyled className="formContainer narrowFields">
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-LEGAL_REP_BENEFICIAL',
            })}
            infoText={
              isAureaActiveClient
                ? TrlFormattedMessagePlain({
                    id: 'TRLT_UI-LEGAL_REP_BENEFICIAL_HINT',
                  })
                : ''
            }
          />
          {values.representatives.map((representative, index) => (
            <ElementContainer key={index} className="elementContainer">
              <ElementHeader>
                <ElementLabel>
                  {`${TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REPR_BENEF',
                  })} ${index + 1}`}
                </ElementLabel>

                {index !== 0 && values.representatives.length === index + 1 && (
                  <TrlPill
                    label={TrlFormattedMessagePlain({ id: 'TRLT_UI-DELETE' })}
                    variant="delete"
                    onClick={() => {
                      setFieldValue(
                        'representatives',
                        values.representatives.slice(0, -1),
                      );
                    }}
                  />
                )}
              </ElementHeader>
              <Box className="flexWrap">
                <TrlFormRadioGroup
                  name={`representatives[${index}].representative`}
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-LEGAL_REPRESENTATIVE',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('representative', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'representative',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'representative',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlFormRadioGroup
                  name={`representatives[${index}].beneficiary`}
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-BENEFICIARY',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('beneficiary', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'beneficiary',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'beneficiary',
                    fieldProperty: 'isRequired',
                  })}
                  extraInfo={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-BENEFICIARY_HINT',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repShare`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_SHARE',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_SHARE',
                  })}
                  id="repShare"
                  isLocked={hasInitialValue('repShare', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repShare',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repShare',
                    fieldProperty: 'isRequired',
                  })}
                  inputProps={{
                    maxLength: 3,
                  }}
                />
                <TrlTextField
                  name={`representatives[${index}].repName`}
                  type="text"
                  label={TrlFormattedMessagePlain({ id: 'TRLT_UI-FULL_NAME' })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-FULL_NAME',
                  })}
                  id="repName"
                  autoComplete="name"
                  isLocked={hasInitialValue('repName', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repName',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repName',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repNationality`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-NATIONALITY',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-NATIONALITY',
                  })}
                  id="repNationality"
                  isLocked={hasInitialValue('repNationality', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repNationality',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repNationality',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repSecNationality`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-OTHER_NATIONALITIES',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-OTHER_NATIONALITIES',
                  })}
                  id="repSecNationality"
                  isLocked={hasInitialValue('repSecNationality', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repSecNationality',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repSecNationality',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlFormDropdown
                  name={`representatives[${index}].repIdType`}
                  label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ID_TYPE' })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-ID_TYPE',
                  })}
                  isLocked={hasInitialValue('repIdType', index)}
                  options={getFieldOptions('repIdType')}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repIdType',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repIdType',
                    fieldProperty: 'isRequired',
                  })}
                  onChangeValue={() => {
                    if (!isFieldHidden.repOtherIdType)
                      setFieldValue(
                        `representatives[${index}].repOtherIdType`,
                        '',
                        false,
                      );
                    if (!isFieldHidden.repIdNum)
                      setFieldValue(
                        `representatives[${index}].repIdNum`,
                        '',
                        false,
                      );
                    if (!isFieldHidden.repIdLocal)
                      setFieldValue(
                        `representatives[${index}].repIdLocal`,
                        '',
                        false,
                      );
                    if (!isFieldHidden.repIdDate)
                      setFieldValue(
                        `representatives[${index}].repIdDate`,
                        '',
                        false,
                      );
                    if (!isFieldHidden.repIdExpDate)
                      setFieldValue(
                        `representatives[${index}].repIdExpDate`,
                        '',
                        false,
                      );
                  }}
                />
                {representative.repIdType === 'OUTRO' && (
                  <TrlTextField
                    name={`representatives[${index}].repOtherIdType`}
                    type="text"
                    label={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-REP_OTHER_ID_TYPE',
                    })}
                    placeholder={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-REP_OTHER_ID_TYPE',
                    })}
                    id="repOtherIdType"
                    isLocked={hasInitialValue('repOtherIdType', index)}
                    isHidden={isFieldHidden.repOtherIdType}
                    isRequired={verifyFieldProperty({
                      fieldName: 'repOtherIdType',
                      fieldProperty: 'isRequired',
                    })}
                  />
                )}
                <TrlTextField
                  name={`representatives[${index}].repIdNum`}
                  type="text"
                  label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ID_NUMBER' })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-ID_NUMBER',
                  })}
                  id="repIdNum"
                  isLocked={hasInitialValue('repIdNum', index)}
                  isHidden={isFieldHidden.repIdNum}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repIdNum',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repIdLocal`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_ID_LOCAL',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_ID_LOCAL',
                  })}
                  id="repIdLocal"
                  isLocked={hasInitialValue('repIdLocal', index)}
                  isHidden={isFieldHidden.repIdLocal}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repIdLocal',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlDatePicker
                  name={`representatives[${index}].repIdDate`}
                  label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ISSUE_DATE' })}
                  value={representative.repIdDate}
                  disableFuture
                  isLocked={hasInitialValue('repIdDate', index)}
                  dateFormatType="date"
                  isHidden={isFieldHidden.repIdDate}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repIdDate',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlDatePicker
                  name={`representatives[${index}].repIdExpDate`}
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-EXPIRATION_DATE',
                  })}
                  value={representative.repIdExpDate}
                  disablePast
                  isLocked={hasInitialValue('repIdExpDate', index)}
                  dateFormatType="date"
                  isHidden={isFieldHidden.repIdExpDate}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repIdExpDate',
                    fieldProperty: 'isRequired',
                  })}
                  warningText={
                    isAfter(representative.repIdExpDate, todaysDate)
                      ? ''
                      : validityExpiredLabel
                  }
                />
                <TrlTextField
                  name={`representatives[${index}].repFatherName`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-FATHER_NAME',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-FATHER_NAME',
                  })}
                  id="repFatherName"
                  isLocked={hasInitialValue('repFatherName', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repFatherName',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repFatherName',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repMotherName`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-MOTHER_NAME',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-MOTHER_NAME',
                  })}
                  id="repMotherName"
                  isLocked={hasInitialValue('repMotherName', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repMotherName',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repMotherName',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repAddress`}
                  type="text"
                  label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ADDRESS' })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-ADDRESS',
                  })}
                  id="repAddress"
                  isLocked={hasInitialValue('repAddress', index)}
                  autoComplete="address-line1"
                  isHidden={verifyFieldProperty({
                    fieldName: 'repAddress',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repAddress',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repPostcodeDescr`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_POST_CODE_DESCR',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_POST_CODE_DESCR',
                  })}
                  id="repPostcodeDescr"
                  isLocked={hasInitialValue('repPostcodeDescr', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repPostcodeDescr',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repPostcodeDescr',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repPostCode`}
                  type="text"
                  autoComplete="postal-code"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_POST_CODE',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_POST_CODE',
                  })}
                  id="repPostCode"
                  isLocked={hasInitialValue('repPostCode', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repPostCode',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repPostCode',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repCountry`}
                  type="text"
                  autoComplete="country"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_COUNTRY',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_COUNTRY',
                  })}
                  id="repCountry"
                  isLocked={hasInitialValue('repCountry', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repCountry',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repCountry',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlFormRadioGroup
                  className="fullWidth"
                  name={`representatives[${index}].repAmericanNationality`}
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REP_AMERICAN_NATIONALITY',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('repAmericanNationality', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repAmericanNationality',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repAmericanNationality',
                    fieldProperty: 'isRequired',
                  })}
                  additionalFunc={(event) => {
                    if (event.target.value === 'N' && !isFieldHidden.repUsTaxId)
                      setFieldValue(
                        `representatives[${index}].repUsTaxId`,
                        '',
                        false,
                      );
                  }}
                />
                {representative.repAmericanNationality === 'Y' && (
                  <TrlTextField
                    className="fullWidth"
                    name={`representatives[${index}].repUsTaxId`}
                    type="text"
                    label={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-REP_US_TAX_ID',
                    })}
                    placeholder={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-REP_US_TAX_ID',
                    })}
                    id="repUsTaxId"
                    isLocked={hasInitialValue('repUsTaxId', index)}
                    isHidden={isFieldHidden.repUsTaxId}
                    isRequired={verifyFieldProperty({
                      fieldName: 'repUsTaxId',
                      fieldProperty: 'isRequired',
                    })}
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                )}
                <TrlTextField
                  name={`representatives[${index}].repJob`}
                  type="text"
                  label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PROFESSION' })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PROFESSION',
                  })}
                  id="repJob"
                  isLocked={hasInitialValue('repJob', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repJob',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repJob',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repRole`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REPRESENTATION_ROLE',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-REPRESENTATION_ROLE',
                  })}
                  id="repRole"
                  isLocked={hasInitialValue('repRole', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repRole',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repRole',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name={`representatives[${index}].repShareholding`}
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-CAPITAL_PARTICIPATION',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-CAPITAL_PARTICIPATION',
                  })}
                  id="repShareholding"
                  isLocked={hasInitialValue('repShareholding', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repShareholding',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repShareholding',
                    fieldProperty: 'isRequired',
                  })}
                  inputProps={{
                    maxLength: 5,
                  }}
                />
                <TrlFormRadioGroup
                  name={`representatives[${index}].repPpe`}
                  label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PPE' })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('repPpe', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repPpe',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repPpe',
                    fieldProperty: 'isRequired',
                  })}
                  extraInfo={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_HINT',
                  })}
                  additionalFunc={(event) => {
                    if (!isFieldHidden.repNoApplOpt)
                      setRepNoApplOptValue({
                        event,
                        fieldsNamePrefix: `representatives[${index}]`,
                        currentFieldNameSufix: 'repPpe',
                        values: representative,
                        setFieldValue,
                      });
                  }}
                />
                <TrlFormRadioGroup
                  name={`representatives[${index}].repPpeFamily`}
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-FAMILIAR_PPE',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('repPpeFamily', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repPpeFamily',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repPpeFamily',
                    fieldProperty: 'isRequired',
                  })}
                  additionalFunc={(event) => {
                    if (!isFieldHidden.repNoApplOpt)
                      setRepNoApplOptValue({
                        event,
                        fieldsNamePrefix: `representatives[${index}]`,
                        currentFieldNameSufix: 'repPpeFamily',
                        values: representative,
                        setFieldValue,
                      });

                    if (
                      event.target.value === 'N' &&
                      !isFieldHidden.repPpeKinshipDeg
                    )
                      setFieldValue(
                        `representatives[${index}].repPpeKinshipDeg`,
                        '',
                        false,
                      );
                  }}
                />
                {representative.repPpeFamily === 'Y' && (
                  <TrlTextField
                    name={`representatives[${index}].repPpeKinshipDeg`}
                    type="text"
                    label={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-KINSHIP_DEGREE',
                    })}
                    placeholder={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-KINSHIP_DEGREE',
                    })}
                    id="repPpeKinshipDeg"
                    isLocked={hasInitialValue('repPpeKinshipDeg', index)}
                    isHidden={isFieldHidden.repPpeKinshipDeg}
                    isRequired={verifyFieldProperty({
                      fieldName: 'repPpeKinshipDeg',
                      fieldProperty: 'isRequired',
                    })}
                  />
                )}
                <TrlFormRadioGroup
                  name={`representatives[${index}].repPpeRelationship`}
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-RELATION_WITH_PPE',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('repPpeRelationship', index)}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repPpeRelationship',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repPpeRelationship',
                    fieldProperty: 'isRequired',
                  })}
                  additionalFunc={(event) => {
                    if (!isFieldHidden.repNoApplOpt)
                      setRepNoApplOptValue({
                        event,
                        fieldsNamePrefix: `representatives[${index}]`,
                        currentFieldNameSufix: 'repPpeRelationship',
                        values: representative,
                        setFieldValue,
                      });
                  }}
                />
                <TrlFormDropdown
                  name={`representatives[${index}].repIncomeSource`}
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-INCOME_NATURE',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-INCOME_NATURE',
                  })}
                  isLocked={hasInitialValue('repIncomeSource', index)}
                  options={getFieldOptions('repIncomeSource')}
                  isHidden={verifyFieldProperty({
                    fieldName: 'repIncomeSource',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'repIncomeSource',
                    fieldProperty: 'isRequired',
                  })}
                />
              </Box>
            </ElementContainer>
          ))}

          <TrlPill
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-ADD_REPRESENTATIVE',
            })}
            variant="addItem"
            onClick={() => {
              setFieldValue('representatives', [
                ...values.representatives,
                Object.keys(values.representatives[0]).reduce((acc, field) => {
                  acc[field] = '';
                  return acc;
                }, {}),
              ]);
            }}
          />

          <TrlFormButtons
            fixedAtBottom
            advanceButtonName="TRLT_UI-NEXT"
            isAdvanceButtonDisabled={isSubmitFormButtonDisabled({
              isSubmitting,
              dirty,
              errors,
              values: values.representatives,
              areFiles: false,
            })}
          />
        </FormStyled>
      )}
    </FormWrapper>
  );
};
